import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { storage } from "../firebase_setup/firebase";
import { ref, getDownloadURL } from "firebase/storage";

import {
  Avatar,
  MediaIcon,
  Pane,
  Spinner
} from 'evergreen-ui';

function CloudImage({ avatar, size, name, src, customClass, displayAs = "original", style }) {
  const [imageToRenderUrl, setImageToRenderUrl] = useState("");
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);

  useEffect(() => {
    let isMounted = true;

    const fetchImage = async () => {
      if (!src || src === "" || src === "https://www.hederis.com/images/nellie.png") {
        if (isMounted) {
          setImageToRenderUrl("");
          setIsLoading(false);
          setImageError(false);
        }
        return;
      }

      if (isMounted) {
        setIsLoading(true);
        setImageError(false);
        setImageToRenderUrl("");
      }

      let finalSrcToLoad = src;

      if (displayAs === 'thumbnail') {
        try {
          if (src.startsWith("https://firebasestorage.googleapis.com/")) {
            const url = new URL(src);
            const bucketName = storage.app.options.storageBucket;
            const pathPrefix = `/v0/b/${bucketName}/o/`;
            let storagePath = decodeURIComponent(url.pathname.replace(pathPrefix, ''));

            const lastDotIndex = storagePath.lastIndexOf('.');
            if (lastDotIndex === -1) {
              console.error("CloudImage: Invalid original image path for thumbnail (no extension)", storagePath);
            } else {
              const basePath = storagePath.substring(0, lastDotIndex);
              const extension = storagePath.substring(lastDotIndex);
              const thumbnailStoragePath = `${basePath}_thumb${extension}`;

              const thumbRef = ref(storage, thumbnailStoragePath);
              finalSrcToLoad = await getDownloadURL(thumbRef);
            }
          } else {
            console.warn(`CloudImage: 'displayAs="thumbnail"' requested for non-Firebase URL: ${src}. Using original.`);
          }
        } catch (error) {
          console.warn(`CloudImage: Thumbnail not found for ${src}, falling back to original. Error:`, error);
        }
      }

      if (finalSrcToLoad.includes('http://') || finalSrcToLoad.includes('https://')) {
        if (isMounted) {
          setImageToRenderUrl(finalSrcToLoad);
          setIsLoading(false);
        }
      } else if (publisherGlobalData.firebaseAuth) {
        try {
          const imgRef = ref(storage, finalSrcToLoad);
          const res = await getDownloadURL(imgRef);
          if (isMounted) setImageToRenderUrl(res);
        } catch (error) {
          console.error("Error loading image from Firebase path:", finalSrcToLoad, error);
          if (isMounted) setImageError(true);
        } finally {
          if (isMounted) setIsLoading(false);
        }
      } else {
        console.error("Cannot load image: src is a path and Firebase auth is not available.", finalSrcToLoad);
        if (isMounted) {
          setImageError(true);
          setIsLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
    };
  }, [src, publisherGlobalData.firebaseAuth, displayAs]);

  const handleImageError = () => {
    if (!imageError) {
      console.error("Image load error for (from img.onError):", imageToRenderUrl);
      setImageError(true);
      setIsLoading(false);
    }
  };

  const showSpinner = isLoading;
  const showPlaceholder = imageError || (!isLoading && !imageToRenderUrl);
  const displayUrl = (!imageError && imageToRenderUrl) ? imageToRenderUrl : null;

  if (avatar) {
    return (
      <Avatar
        src={displayUrl}
        size={size || 50}
        name={name || "User"}
      />
    );
  }

  if (showSpinner) {
    return (
      <Pane
        className={`profileHolder${customClass || ''}`}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "var(--medgrey)",
          width: size,
          height: size
        }}
      >
        <Spinner size={16} />
      </Pane>
    );
  }

  if (displayUrl) {
    return (
      <Pane className={`profileHolder${customClass || ''}`}>
        <img
          className={`profile${customClass || ''}`}
          src={displayUrl}
          alt={name || "Image"}
          style={{
            width: size,
            height: size,
            borderRadius: avatar ? '50%' : '0',
            objectFit: 'cover',
            ...style
          }}
          onError={handleImageError}
        />
      </Pane>
    );
  }

  return (
    <Pane
      className={`profileHolder${customClass || ''}`}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "var(--medgrey)",
        width: size,
        height: size
      }}
    >
      <MediaIcon size={16} color="var(--darkgrey)" />
    </Pane>
  );
}

export default CloudImage;
